<template>
  <b-modal
    id="template_field_types_modal"
    @show="onShow"
    @hide="onClose"
    @hidden="resetDataModal"
    centered
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    scrollable
    size="lg"
  >
    <p class="h3 text-capitalize">
      {{ $t("risk.template.field_types") }}
    </p>
    <hr />
    <div class="p-1">
      <div class="row row-cols-2 align-self-center containerSelect">
        <div
          v-for="fType in activeFieldTypes"
          :key="fType.type"
          class="col p-1 mx-1"
          :class="{ active: fieldType === fType.type }"
        >
          <label :for="fType.type" class="d-flex align-items-center column">
            <input
              :id="fType.type"
              type="radio"
              v-model="fieldType"
              :value="fType.type"
              class="fTypeSelector"
            />

            <!-- icon that supports SVG or Feather icon names -->
            <template v-if="isSvgIcon(fType.icon)">
              <img
                :src="require(`@/assets/images/icons/template-builder/${fType.icon}`)"
                alt="Icono"
                class="iconStyle"
              />
            </template>
            <template v-else>
              <feather-icon :icon="fType.icon || 'SquareIcon'" size="20" class="mr-1" />
            </template>

            <span>{{ $t(fType.name) }}</span>
          </label>
        </div>
      </div>
    </div>

    <div>
      <template v-if="fieldTypeConfig">
        <hr />
        <div>{{ $t("risk.template.field_type_config") }}</div>
        <div class="pt-2 px-2">
          <field-type-config
            :field-type-cfg="localFieldTypeCfg"
            :config="fieldTypeConfig"
            @update:data="onDataUpdated"
          />
        </div>
      </template>
    </div>

    <template #modal-footer="{ close }">
      <b-button variant="danger" size="sm" @click="close()">
        {{ $t("close") }}
      </b-button>
      <b-button variant="primary" size="sm" @click="onApply(close)">
        {{ $t("actions.save") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { BModal } from "bootstrap-vue";
import { ref, watch } from "@vue/composition-api";
import FieldTypeConfig from "./FieldTypeConfig";
import { fieldTypes } from "./config";

export default {
  components: {
    BModal,
    FieldTypeConfig,
  },
  props: {
    modalName: String,
    type: String,
    fieldTypeCfg: Object,
  },
  setup(props, { emit }) {
    const fieldType = ref(props.type || "");
    const fieldTypeConfig = ref(null);
    const localFieldTypeCfg = ref({ data: [] }); // always normalized to { data: [...] }
    const activeFieldTypes = fieldTypes;

    // normalize any incoming cfg into { data: [...] }
    const normalizeCfg = (src) => {
      // if already array
      if (Array.isArray(src)) return { data: src };
      // if { data: ... }
      if (src && Array.isArray(src.data)) return { data: src.data };
      // if { data: '[]' } as string
      if (src && typeof src.data === "string") {
        try {
          return { data: JSON.parse(src.data) || [] };
        } catch {
          return { data: [] };
        }
      }
      // if { fieldTypeCfg: [...] } (legacy passed by mistake)
      if (src && Array.isArray(src.fieldTypeCfg)) return { data: src.fieldTypeCfg };
      // if nothing or unknown shape
      return { data: [] };
    };

    const setTypeConfig = (t) => {
      // set current type config so the editor renders
      fieldTypeConfig.value = fieldTypes.find((fType) => fType.type === t) || null;
    };

    const onShow = () => {
      fieldType.value = props.type || "";
      localFieldTypeCfg.value = normalizeCfg(props.fieldTypeCfg || {});
      setTypeConfig(fieldType.value);
    };

    const onClose = () => {};
    const resetDataModal = () => {};

    // single-line: accept both array and object payloads from child
    const onDataUpdated = (payload) => {
      localFieldTypeCfg.value = normalizeCfg(payload);
    };

    // single-line: shallow clone on apply to avoid shared refs
    const onApply = (close) => {
      emit("selected");
      emit("update:type", fieldType.value);
      emit("update:fieldTypeCfg", {
        ...localFieldTypeCfg.value,
        data: [...(localFieldTypeCfg.value.data || [])],
      });
      close();
    };

    // checks if the icon is a svg file or a FeatherIxon
    const isSvgIcon = (icon) => typeof icon === "string" && /\.svg$/i.test(icon);

    // only reset options when type actually changes (not on first mount)
    watch(fieldType, (val, oldVal) => {
      setTypeConfig(val); // always refresh right panel
      if (oldVal && val !== oldVal) {
        // when changing type intentionally, start with empty option list
        localFieldTypeCfg.value = { data: [] };
      }
    });

    return {
      onShow,
      onClose,
      resetDataModal,
      onDataUpdated,
      onApply,
      fieldType,
      activeFieldTypes,
      fieldTypeConfig,
      localFieldTypeCfg,
      isSvgIcon,
    };
  },
};
</script>

<style lang="scss" scoped>
.fTypeSelector {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding: 0.4rem;
}
.iconStyle {
  width: 30px;
  height: 30px;
  margin-right: 5px;
}
.containerSelect :hover {
  background-color: #cce8ff;
  color: #1b3d5e;
  border-radius: 5px;
}
.active {
  background-color: #cce8ff;
  color: #1b3d5e;
  border-radius: 5px;
}

.dark-layout {
  color: white;
  .containerSelect :hover {
    background-color: #cce8ff;
    color: #1b3d5e;
  }
  .active span {
    color: #1b3d5e;
  }
}
</style>
